export type Category =
  'all' | 'favorites' | 'converters' | 'text' | 'json' | 'data' | 'color' | 'image' | 'code' | 'crypto' | 'misc' | 'network' | 'seo';

export type Tool = {
  id: string;
  name: string;
  description: string;
  category: Category;
  type: 'internal' | 'external';
  path?: string;
  url?: string;
  icon: string;
};

export const categories: { id: Category; name: string; icon: string; description: string }[] = [
  {
    id: 'all',
    name: 'All Tools',
    icon: 'LayoutGrid',
    description:
      'Browse all available developer tools. Custom tools run locally in your browser, external tools open trusted third-party services in a new tab.',
  },
  {
    id: 'favorites',
    name: 'Favorites',
    icon: 'Star',
    description: 'Your favorite tools for quick access. Click the star icon on any tool to add it to your favorites.',
  },
  {
    id: 'converters',
    name: 'Converters',
    icon: 'RefreshCw',
    description:
      'Convert files and formats. Encode images, documents, and other files to Base64 for embedding in code, CSS, or data URIs.',
  },
  {
    id: 'text',
    name: 'Text',
    icon: 'Type',
    description:
      'Text manipulation and encoding tools. Encode/decode strings, transform text case, generate placeholder content, and compare text differences.',
  },
  {
    id: 'json',
    name: 'JSON',
    icon: 'Braces',
    description:
      'JSON processing utilities. Format, validate, minify JSON data, and validate against JSON schemas for API development and data validation.',
  },
  {
    id: 'data',
    name: 'Data',
    icon: 'Database',
    description:
      'Data parsing and formatting tools. Process CSV files, format XML documents, and work with structured data formats commonly used in development.',
  },
  {
    id: 'color',
    name: 'Color',
    icon: 'Palette',
    description:
      'Color tools for designers and developers. Pick colors, convert between formats (HEX, RGB, HSL, OKLCH), generate palettes, and check WCAG accessibility compliance.',
  },
  {
    id: 'image',
    name: 'Image',
    icon: 'Image',
    description:
      'Image processing and optimization. Compress images, optimize SVG files, and manipulate graphics for better web performance.',
  },
  {
    id: 'code',
    name: 'Code',
    icon: 'Code',
    description:
      'Development utilities. Test regular expressions, minify JavaScript, check browser compatibility, and analyze npm package sizes.',
  },
  {
    id: 'crypto',
    name: 'Crypto',
    icon: 'Lock',
    description:
      'Cryptography and security tools. Generate hashes (MD5, SHA-256), create UUIDs, decode JWT tokens, and work with cryptographic operations.',
  },
  {
    id: 'misc',
    name: 'Misc',
    icon: 'Sparkles',
    description:
      "Miscellaneous utilities. Generate QR codes, preview Markdown, and access other helpful tools that don't fit into specific categories.",
  },
  {
    id: 'network',
    name: 'Network',
    icon: 'Network',
    description:
      'Network and API tools for developers. Parse URLs, analyze HTTP headers, test User-Agent strings, and debug network requests.',
  },
  {
    id: 'seo',
    name: 'SEO',
    icon: 'TrendingUp',
    description:
      'SEO and marketing tools. Build UTM tracking URLs, reference social media image sizes, generate meta tags, and optimize web presence.',
  },
];

export const tools: Tool[] = [
  // Converter Tools
  {
    id: 'file-to-base64',
    name: 'File to Base64',
    description:
      'Encode files and remote URLs to Base64 with 11 output formats including data URIs, CSS backgrounds, HTML embeds, and JSON payloads. Supports drag-and-drop, image preview, and handles files up to 10MB for embedding assets directly in code.',
    category: 'converters',
    type: 'internal',
    path: '/tool/file-to-base64',
    icon: 'FileUp',
  },
  {
    id: 'timestamp-converter',
    name: 'Timestamp Converter',
    description:
      'Bidirectional Unix timestamp converter with auto-detection of seconds vs milliseconds format. Features live current timestamp, relative time calculation, and exports to ISO 8601, UTC, local time, and date-only formats for API testing and logging.',
    category: 'converters',
    type: 'internal',
    path: '/tool/timestamp-converter',
    icon: 'Clock',
  },
  {
    id: 'unit-converter',
    name: 'Unit Converter',
    description:
      'Convert between 12 unit categories including length, mass, temperature, area, volume, time, speed, data, pressure, energy, power, and frequency. Supports bidirectional conversion with adjustable precision and instant unit swapping.',
    category: 'converters',
    type: 'internal',
    path: '/tool/unit-converter',
    icon: 'Ruler',
  },
  {
    id: 'font-to-base64',
    name: 'Font to Base64',
    description:
      'Generate Base64-encoded @font-face CSS declarations from WOFF, WOFF2, TTF, and OTF files. Extracts font metadata, supports advanced CSS properties (unicode-range, font-display, metric overrides), and includes live preview with custom text.',
    category: 'converters',
    type: 'internal',
    path: '/tool/font-to-base64',
    icon: 'FileType',
  },
  {
    id: 'currency-converter',
    name: 'Currency Converter',
    description:
      'Real-time multi-currency converter with live exchange rates from multiple API sources. Compare unlimited currencies simultaneously in a responsive grid, with bidirectional conversion, 1-hour rate caching, and configurable precision for financial calculations.',
    category: 'converters',
    type: 'internal',
    path: '/tool/currency-converter',
    icon: 'Coins',
  },
  {
    id: 'json-to-xml',
    name: 'JSON to XML',
    description:
      'Convert JSON to XML with customizable root element, attribute mode (none/all/underscore prefix), namespace support, and pretty print options. Features live conversion, special key syntax (@attr, #text), and automatic XML character escaping.',
    category: 'converters',
    type: 'internal',
    path: '/tool/json-to-xml',
    icon: 'FileCode2',
  },
  {
    id: 'toml-parser',
    name: 'TOML Parser',
    description:
      'Parse and validate TOML configuration files with instant formatting, JSON conversion, table structure visualization, and date format preview. Features syntax validation, pretty print options, download as JSON, and detailed analysis of tables and date values.',
    category: 'converters',
    type: 'internal',
    path: '/tool/toml-parser',
    icon: 'FileCode',
  },
  {
    id: 'sql-to-mongo',
    name: 'SQL to MongoDB Query',
    description:
      'Convert SQL SELECT queries to MongoDB find() or aggregation pipeline syntax. Supports WHERE clauses with AND/OR operators, JOINs (converted to $lookup), GROUP BY with aggregate functions (COUNT, SUM, AVG), ORDER BY, LIMIT, OFFSET, projection mapping, and common SQL operators (=, !=, >, <, >=, <=, IN, LIKE). Includes operator reference and example queries for database migration and learning.',
    category: 'converters',
    type: 'internal',
    path: '/tool/sql-to-mongo',
    icon: 'Database',
  },
  {
    id: 'color-space-converter',
    name: 'Color Space Converter',
    description:
      'Convert colors between sRGB, Display P3, and Adobe RGB color spaces with live preview and side-by-side comparison. Features gamut warning for out-of-range colors, accurate color space transformations using XYZ intermediate space, and CSS color() syntax output for wide-gamut displays.',
    category: 'converters',
    type: 'internal',
    path: '/tool/color-space-converter',
    icon: 'Palette',
  },

  // Text Tools
  {
    id: 'base64',
    name: 'Base64 Encoder/Decoder',
    description:
      'Encode and decode Base64 strings for data URLs, API tokens, and embedded content. Supports bidirectional conversion with error handling for invalid inputs.',
    category: 'text',
    type: 'internal',
    path: '/tool/base64',
    icon: 'Binary',
  },
  {
    id: 'base32',
    name: 'Base32 Encoder/Decoder',
    description:
      'RFC 4648 compliant Base32 encoder/decoder with padding options, case-insensitive decode, and batch processing. Perfect for TOTP secrets, Tor addresses, and human-readable identifiers.',
    category: 'text',
    type: 'internal',
    path: '/tool/base32',
    icon: 'Binary',
  },
  {
    id: 'url-encoder',
    name: 'URL Encoder/Decoder',
    description:
      'Encode and decode URL components, query parameters, and form data. Safely handles special characters for web requests and APIs.',
    category: 'text',
    type: 'internal',
    path: '/tool/url-encoder',
    icon: 'Link',
  },
  {
    id: 'case-converter',
    name: 'Case Converter',
    description:
      'Convert text between camelCase, snake_case, PascalCase, kebab-case, CONSTANT_CASE, and more. Essential for refactoring code and adapting naming conventions across languages.',
    category: 'text',
    type: 'internal',
    path: '/tool/case-converter',
    icon: 'CaseSensitive',
  },
  {
    id: 'lorem-ipsum',
    name: 'Lorem Ipsum Generator',
    description:
      'Generate customizable placeholder text in paragraphs, sentences, or words. Perfect for mockups, UI testing, and design prototypes.',
    category: 'text',
    type: 'internal',
    path: '/tool/lorem-ipsum',
    icon: 'Text',
  },
  {
    id: 'text-counter',
    name: 'Text Counter',
    description:
      'Analyze text with real-time character, word, line, and sentence counts. Includes reading time estimates and statistical insights for content writers and developers.',
    category: 'text',
    type: 'internal',
    path: '/tool/text-counter',
    icon: 'Calculator',
  },
  {
    id: 'string-manipulation',
    name: 'String Manipulation',
    description:
      'Perform common text operations including reverse, sort, remove duplicates, trim whitespace, and case transformations. Streamlines bulk text processing tasks.',
    category: 'text',
    type: 'internal',
    path: '/tool/string-manipulation',
    icon: 'WrapText',
  },
  {
    id: 'text-to-binary-hex',
    name: 'Text to Binary/Hex',
    description:
      'Convert text to binary, hexadecimal, or octal representations and back. Useful for debugging, low-level programming, and understanding data encoding.',
    category: 'text',
    type: 'internal',
    path: '/tool/text-to-binary-hex',
    icon: 'Binary',
  },
  {
    id: 'slug-generator',
    name: 'Slug Generator',
    description:
      'Generate SEO-friendly URL slugs from any text with customizable separators and character handling. Automatically removes special characters and handles multiple languages.',
    category: 'text',
    type: 'internal',
    path: '/tool/slug-generator',
    icon: 'Link2',
  },
  {
    id: 'regex-replace',
    name: 'Regex Replace',
    description:
      'Find and replace text using regular expressions with support for flags (global, case-insensitive, multiline). Features live preview to test patterns before applying.',
    category: 'text',
    type: 'internal',
    path: '/tool/regex-replace',
    icon: 'Replace',
  },
  {
    id: 'unicode-converter',
    name: 'Unicode Converter',
    description:
      'Convert between text and Unicode code points in multiple formats (U+XXXX, \\uXXXX, HTML entities). Essential for internationalization and character debugging.',
    category: 'text',
    type: 'internal',
    path: '/tool/unicode-converter',
    icon: 'Code2',
  },
  {
    id: 'html-stripper',
    name: 'HTML Stripper',
    description:
      'Extract clean plain text from HTML by removing tags and decoding entities. Perfect for content extraction, search indexing, and text analysis.',
    category: 'text',
    type: 'internal',
    path: '/tool/html-stripper',
    icon: 'FileCode',
  },
  {
    id: 'html-encoder',
    name: 'HTML Encoder/Decoder',
    description:
      'Encode and decode HTML entities using named (&amp;) or numeric (&#38;) formats. Prevents XSS attacks and ensures safe display of user-generated content.',
    category: 'text',
    type: 'internal',
    path: '/tool/html-encoder',
    icon: 'Code',
  },
  {
    id: 'markdown-to-html',
    name: 'Markdown to HTML',
    description:
      'Convert Markdown to HTML with live preview and GitHub Flavored Markdown support. Includes tables, task lists, syntax highlighting, and auto-linking.',
    category: 'text',
    type: 'internal',
    path: '/tool/markdown-to-html',
    icon: 'FileCode',
  },
  {
    id: 'text-template-engine',
    name: 'Text Template Engine',
    description:
      'Create reusable templates with variable placeholders and populate them using JSON data or form inputs. Ideal for email templates, code generation, and batch content creation.',
    category: 'text',
    type: 'internal',
    path: '/tool/text-template-engine',
    icon: 'FileText',
  },
  {
    id: 'remove-duplicates',
    name: 'Remove Duplicates',
    description:
      'Clean text lists by removing duplicate lines with options for case sensitivity, trimming, and sorting. Streamlines data cleanup for imports and configuration files.',
    category: 'text',
    type: 'internal',
    path: '/tool/remove-duplicates',
    icon: 'ListFilter',
  },
  {
    id: 'line-sorter',
    name: 'Line Sorter',
    description:
      'Sort text lines alphabetically, numerically, or with natural sorting (file1.txt, file2.txt, file10.txt). Supports reverse order and case-sensitive sorting.',
    category: 'text',
    type: 'internal',
    path: '/tool/line-sorter',
    icon: 'ArrowUpDown',
  },
  {
    id: 'ascii-art-generator',
    name: 'ASCII Art Generator',
    description:
      'Generate ASCII art from text and images. Create banner text, logos, and decorative art for terminal applications, code comments, and retro designs.',
    category: 'text',
    type: 'external',
    url: 'https://www.ascii-art-generator.org/',
    icon: 'Sparkles',
  },
  {
    id: 'diff-checker',
    name: 'Text Diff Checker',
    description:
      'Compare two text blocks side-by-side with highlighted additions, deletions, and modifications. Essential for code reviews, content comparison, and tracking changes.',
    category: 'text',
    type: 'external',
    url: 'https://www.diffchecker.com/',
    icon: 'FileText',
  },

  // JSON Tools
  {
    id: 'json-formatter',
    name: 'JSON Formatter',
    description:
      'Format and minify JSON with syntax validation, customizable indentation (2/4 spaces or tabs), and detailed error detection for debugging malformed data',
    category: 'json',
    type: 'internal',
    path: '/tool/json-formatter',
    icon: 'FileJson',
  },
  {
    id: 'json-to-typescript',
    name: 'JSON to TypeScript',
    description:
      'Generate TypeScript interfaces from JSON with automatic type inference, nested interface extraction, and custom root naming for type-safe development',
    category: 'json',
    type: 'internal',
    path: '/tool/json-to-typescript',
    icon: 'FileType',
  },
  {
    id: 'json-path-tester',
    name: 'JSON Path Tester',
    description:
      'Test and debug JSONPath expressions with interactive query builder, example patterns, syntax guide, and real-time match counting for complex data extraction',
    category: 'json',
    type: 'internal',
    path: '/tool/json-path-tester',
    icon: 'Route',
  },
  {
    id: 'json-diff',
    name: 'JSON Diff',
    description:
      'Compare two JSON objects with visual diff highlighting for added, removed, and changed properties. Features tree and unified views, array order comparison, expand/collapse controls, and path navigation for debugging API responses and configuration changes',
    category: 'json',
    type: 'internal',
    path: '/tool/json-diff',
    icon: 'GitCompare',
  },
  {
    id: 'json-minifier',
    name: 'JSON Minifier',
    description:
      'Minify JSON with validation, size comparison, and compression statistics. Features JSON5 comment removal, whitespace analysis, before/after byte count, and compression ratio visualization for optimizing API payloads and reducing bandwidth',
    category: 'json',
    type: 'internal',
    path: '/tool/json-minifier',
    icon: 'Minimize2',
  },
  {
    id: 'json-schema-generator',
    name: 'JSON Schema Generator',
    description:
      'Generate JSON Schema draft-07 from JSON with automatic type inference, required fields detection, pattern recognition (email/URL/date/UUID), enum extraction, and description placeholders for API validation and documentation',
    category: 'json',
    type: 'internal',
    path: '/tool/json-schema-generator',
    icon: 'FileCode',
  },
  {
    id: 'json-validator',
    name: 'JSON Schema Validator',
    description:
      'Validate JSON documents against JSON Schema specifications to ensure data structure compliance and catch validation errors before deployment',
    category: 'json',
    type: 'external',
    url: 'https://www.jsonschemavalidator.net/',
    icon: 'CheckCircle',
  },

  // Data Tools
  {
    id: 'table-converter',
    name: 'Table Converter',
    description:
      'Convert and edit tables across 11+ formats (JSON, CSV, SQL, XML, Markdown, Excel, YAML) with inline editing, undo/redo, sorting, filtering, and keyboard shortcuts',
    category: 'data',
    type: 'internal',
    path: '/tool/table-converter',
    icon: 'Table2',
  },
  {
    id: 'xml-formatter',
    name: 'XML Formatter',
    description:
      'Format and beautify XML with configurable indentation, text processing modes, 40+ encoding options, and drag-and-drop file upload',
    category: 'data',
    type: 'internal',
    path: '/tool/xml-formatter',
    icon: 'FileCode',
  },
  {
    id: 'sql-formatter',
    name: 'SQL Formatter',
    description:
      'Beautify SQL queries with support for 18 dialects (PostgreSQL, MySQL, BigQuery), keyword case transformation, and smart indentation',
    category: 'data',
    type: 'internal',
    path: '/tool/sql-formatter',
    icon: 'Database',
  },
  {
    id: 'data-faker',
    name: 'Data Faker',
    description:
      'Generate realistic test data in JSON, CSV, SQL, or TypeScript with 20+ locales, 9 data categories, and up to 1000 records for development and testing',
    category: 'data',
    type: 'internal',
    path: '/tool/data-faker',
    icon: 'Shuffle',
  },
  {
    id: 'api-mock-data-generator',
    name: 'API Mock Data Generator',
    description:
      'Build custom API mock data with schema builder, Faker.js integration, nested objects, field relations, and export to JSON or CSV for realistic API testing',
    category: 'data',
    type: 'internal',
    path: '/tool/api-mock-data-generator',
    icon: 'Workflow',
  },
  {
    id: 'yaml-formatter',
    name: 'YAML Formatter',
    description:
      'Format and validate YAML with syntax highlighting, customizable indentation (2/4 spaces), sort keys option, block/flow style conversion, and anchor/alias validation for configuration files',
    category: 'data',
    type: 'internal',
    path: '/tool/yaml-formatter',
    icon: 'FileCode',
  },
  {
    id: 'excel-viewer',
    name: 'Excel Viewer',
    description:
      'View and analyze Excel files in the browser with multi-sheet support, column sorting, search/filter capabilities, and export to CSV or JSON. Automatically displays computed formula values for quick data exploration.',
    category: 'data',
    type: 'internal',
    path: '/tool/excel-viewer',
    icon: 'FileSpreadsheet',
  },
  {
    id: 'log-analyzer',
    name: 'Log Analyzer',
    description:
      'Parse and analyze log files with automatic format detection (Apache, Nginx, JSON, Syslog, Windows Event, Custom). Features real-time filtering by log level, timestamp range, keyword search, statistical analysis with charts, error pattern detection, and export filtered results to JSON/CSV.',
    category: 'data',
    type: 'internal',
    path: '/tool/log-analyzer',
    icon: 'FileSearch',
  },

  // Color Tools
  {
    id: 'color-picker',
    name: 'Color Picker',
    description:
      'Interactive color picker with harmonies (analogous, complementary, triadic), variations (shades, tints, tones, Tailwind scales), WCAG contrast checking, and color blindness simulation for all four types',
    category: 'color',
    type: 'internal',
    path: '/tool/color-picker',
    icon: 'Pipette',
  },
  {
    id: 'color-converter',
    name: 'Color Converter',
    description:
      'Convert colors between HEX, RGB, HSL, HSV, CMYK, and OKLCH formats with live visual preview, interactive color picker, and alpha channel support for transparency',
    category: 'color',
    type: 'internal',
    path: '/tool/color-converter',
    icon: 'Palette',
  },
  {
    id: 'gradient-generator',
    name: 'Gradient Generator',
    description:
      'Create CSS gradients with multi-stop color controls, adjustable angles for linear gradients, radial positioning, live preview, and export as CSS, inline styles, or Tailwind config',
    category: 'color',
    type: 'internal',
    path: '/tool/gradient-generator',
    icon: 'Blend',
  },
  {
    id: 'css-color-names',
    name: 'CSS Color Names',
    description:
      "Browse and search all 147 named CSS colors with instant copy for color names, HEX values, and RGB codes—one-click access to semantic colors like 'tomato' and 'steelblue'",
    category: 'color',
    type: 'internal',
    path: '/tool/css-color-names',
    icon: 'Paintbrush',
  },
  {
    id: 'tailwind-color-palette',
    name: 'Tailwind Color Palette',
    description:
      'Generate complete Tailwind CSS color scales (50-950) from any base color with live UI component preview, import/export JSON palettes, and export as CSS variables or Tailwind config',
    category: 'color',
    type: 'internal',
    path: '/tool/tailwind-color-palette',
    icon: 'Palette',
  },
  {
    id: 'color-blindness-simulator',
    name: 'Color Blindness Simulator',
    description:
      'Simulate protanopia, deuteranopia, tritanopia, and achromatopsia on individual colors or full images with adjustable severity slider, accessible color suggestions, and downloadable results',
    category: 'color',
    type: 'internal',
    path: '/tool/color-blindness-simulator',
    icon: 'Eye',
  },
  {
    id: 'color-palette-generator',
    name: 'Color Palette Generator',
    description:
      'Extract dominant colors from images using K-means clustering, median cut, or quantization algorithms—generates palettes with color percentages and exports to CSS, Tailwind, or JSON',
    category: 'color',
    type: 'internal',
    path: '/tool/color-palette-generator',
    icon: 'Palette',
  },
  {
    id: 'contrast-checker',
    name: 'Contrast Checker',
    description:
      'Verify WCAG AA/AAA compliance for text and UI components with live contrast ratio calculation, side-by-side preview at multiple font sizes, accessible color suggestions, and quick color swapping',
    category: 'color',
    type: 'internal',
    path: '/tool/contrast-checker',
    icon: 'Eye',
  },
  {
    id: 'color-scheme-generator',
    name: 'Color Scheme Generator',
    description:
      'Generate harmonious color palettes using color theory—monochromatic (shades, tints, tones), analogous, complementary, triadic, tetradic, and square harmonies with lockable colors and multi-format export',
    category: 'color',
    type: 'internal',
    path: '/tool/color-scheme-generator',
    icon: 'Palette',
  },
  {
    id: 'brand-color-extractor',
    name: 'Brand Color Extractor',
    description:
      'Extract brand colors from logos by uploading images or clicking directly on the image to pick colors, then generate extended palettes with shades, tints, harmonies, and export to CSS, SCSS, or Tailwind',
    category: 'color',
    type: 'internal',
    path: '/tool/brand-color-extractor',
    icon: 'Pipette',
  },
  {
    id: 'hex-alpha-converter',
    name: 'Hex Alpha Converter',
    description:
      'Convert between 8-digit hex colors with alpha channel and RGBA format. Browse comprehensive alpha reference table with percentage values, search by opacity, export custom palettes, and visualize transparency levels with live preview.',
    category: 'color',
    type: 'internal',
    path: '/tool/hex-alpha-converter',
    icon: 'Droplet',
  },

  // Image Tools
  {
    id: 'image-to-base64',
    name: 'Image to Base64',
    description:
      'Convert images to Base64 data URIs with format conversion (WebP, JPEG, PNG), quality control, resize options, and multiple output formats (full data URI, base64-only, CSS, HTML). Includes live preview and clipboard paste support.',
    category: 'image',
    type: 'internal',
    path: '/tool/image-to-base64',
    icon: 'ImageUp',
  },
  {
    id: 'placeholder-generator',
    name: 'Placeholder Image Generator',
    description:
      'Generate custom placeholder images with text, gradients, or uploaded images. Features advanced gradients (linear, radial, conic), size presets (OG images, avatars, banners), auto-incrementing numbers for batch generation, and export to PNG/JPEG/WebP.',
    category: 'image',
    type: 'internal',
    path: '/tool/placeholder-generator',
    icon: 'ImagePlus',
  },
  {
    id: 'exif-viewer',
    name: 'EXIF Data Viewer',
    description:
      'Extract and display EXIF metadata from images including GPS location, camera settings, and timestamps. Remove all metadata to protect privacy before sharing photos online. Supports JPEG, PNG, TIFF, WebP, and HEIC formats.',
    category: 'image',
    type: 'internal',
    path: '/tool/exif-viewer',
    icon: 'Info',
  },
  {
    id: 'image-cropper',
    name: 'Image Cropper',
    description:
      'Crop images with precision using custom aspect ratios (1:1, 16:9, free), rotate and flip transformations, and circular or rectangular crop shapes. Features live preview, zoom controls, and export to PNG/JPEG/WebP with quality adjustment.',
    category: 'image',
    type: 'internal',
    path: '/tool/image-cropper',
    icon: 'Crop',
  },
  {
    id: 'image-compressor',
    name: 'Image Compressor',
    description:
      'Compress images using browser-native Canvas API to WebP, AVIF, JPEG, and PNG formats. Features single and batch processing modes, quality presets, resize with multiple algorithms, side-by-side comparison, and real-time compression statistics.',
    category: 'image',
    type: 'internal',
    path: '/tool/image-compressor',
    icon: 'FileImage',
  },
  {
    id: 'image-details',
    name: 'Image Details',
    description:
      'Analyze image metadata and properties including dimensions, file size, format, color depth, DPI, aspect ratio, and compression ratio. Batch process multiple images, generate srcset sizes for responsive images, export details to JSON/CSV, and compare images side-by-side.',
    category: 'image',
    type: 'internal',
    path: '/tool/image-details',
    icon: 'Info',
  },
  {
    id: 'responsive-image-generator',
    name: 'Responsive Image Generator',
    description:
      'Generate responsive image sets with multiple sizes and formats (WebP, AVIF, JPEG, PNG) for modern web performance. Creates srcset attributes, picture elements with art direction, automatic size presets, quality control per format, and downloads as ZIP archive with HTML code snippets.',
    category: 'image',
    type: 'internal',
    path: '/tool/responsive-image-generator',
    icon: 'Monitor',
  },
  {
    id: 'squoosh',
    name: 'Squoosh (Recommended)',
    description:
      "Google's advanced image compression tool with best-in-class codecs and superior optimization algorithms. Recommended for maximum quality and smallest file sizes when you need the best compression available.",
    category: 'image',
    type: 'external',
    url: 'https://squoosh.app/',
    icon: 'Sparkles',
  },
  {
    id: 'svg-optimizer',
    name: 'SVG Optimizer',
    description:
      'Optimize and clean SVG files by removing unnecessary metadata, comments, and unused elements. Reduces file size while maintaining visual quality. Uses SVGOMG by Jake Archibald for reliable SVG minification.',
    category: 'image',
    type: 'external',
    url: 'https://jakearchibald.github.io/svgomg/',
    icon: 'Shapes',
  },
  {
    id: 'pdf-to-images',
    name: 'PDF to Images',
    description:
      'Extract pages from PDF documents and convert them to PNG images. Useful for creating thumbnails, previewing PDF content, or converting document pages to web-friendly image format for embedding.',
    category: 'image',
    type: 'external',
    url: 'https://pdf2png.com',
    icon: 'FileScan',
  },
  {
    id: 'video-to-gif',
    name: 'Video to GIF',
    description:
      'Convert video files to animated GIF format with compression and optimization. Perfect for creating shareable animations, demos, or previews from video content for social media and documentation.',
    category: 'image',
    type: 'external',
    url: 'https://ezgif.com/video-to-gif',
    icon: 'Film',
  },

  // Code Tools
  {
    id: 'code-formatter',
    name: 'Code Formatter',
    description:
      'Format and beautify code using Prettier across 8 languages (JS, TS, CSS, HTML, JSON, Markdown, YAML, GraphQL). Perfect for unminifying production code, fixing inconsistent styling, and enforcing team code standards.',
    category: 'code',
    type: 'internal',
    path: '/tool/code-formatter',
    icon: 'Code2',
  },
  {
    id: 'cron-generator',
    name: 'Cron Expression Generator',
    description:
      'Build cron expressions with visual controls and quick presets, then see human-readable explanations and next execution times. Eliminates cron syntax guesswork for scheduling tasks, backups, and automated jobs.',
    category: 'code',
    type: 'internal',
    path: '/tool/cron-generator',
    icon: 'Clock',
  },
  {
    id: 'box-shadow-generator',
    name: 'Box Shadow Generator',
    description:
      'Design CSS box shadows with interactive visual controls for offset, blur, spread, and color. Layer multiple shadows and copy production-ready CSS instantly without trial-and-error coding.',
    category: 'code',
    type: 'internal',
    path: '/tool/box-shadow-generator',
    icon: 'Box',
  },
  {
    id: 'border-radius-generator',
    name: 'Border Radius Generator',
    description:
      'Create CSS border-radius values with visual corner controls and live shape preview. Control each corner independently or sync all four for perfect rounded corners and complex shapes.',
    category: 'code',
    type: 'internal',
    path: '/tool/border-radius-generator',
    icon: 'RoundedCorner',
  },
  {
    id: 'git-cheatsheet',
    name: 'Git Commands Cheatsheet',
    description:
      'Searchable reference of essential Git commands organized by category with descriptions and practical examples. Quick lookup for commits, branches, merges, rebasing, and troubleshooting common Git scenarios.',
    category: 'code',
    type: 'internal',
    path: '/tool/git-cheatsheet',
    icon: 'GitBranch',
  },
  {
    id: 'http-status-codes',
    name: 'HTTP Status Codes',
    description:
      'Comprehensive HTTP status code reference with explanations, common causes, and debugging guidance. Quickly understand 2xx success, 3xx redirects, 4xx client errors, and 5xx server errors when troubleshooting APIs.',
    category: 'code',
    type: 'internal',
    path: '/tool/http-status-codes',
    icon: 'Globe',
  },
  {
    id: 'css-units-converter',
    name: 'CSS Units Converter',
    description:
      'Convert between 33 CSS length units including px, rem, em, vh, vw, and modern viewport variants (dvh, lvh, svh). Essential for responsive design, accessibility, and understanding unit relationships across contexts.',
    category: 'code',
    type: 'internal',
    path: '/tool/css-units-converter',
    icon: 'Ruler',
  },
  {
    id: 'grid-generator',
    name: 'Grid Generator',
    description:
      'Build CSS Grid layouts with visual controls for rows, columns, gaps, and alignment. Generate production-ready grid code without memorizing syntax, perfect for complex responsive layouts.',
    category: 'code',
    type: 'internal',
    path: '/tool/grid-generator',
    icon: 'LayoutGrid',
  },
  {
    id: 'flexbox-generator',
    name: 'Flexbox Generator',
    description:
      'Design CSS Flexbox layouts with interactive controls for direction, alignment, wrapping, and item ordering. Visualize flex behavior and copy working CSS without wrestling with flexbox properties.',
    category: 'code',
    type: 'internal',
    path: '/tool/flexbox-generator',
    icon: 'Rows3',
  },
  {
    id: 'easing-functions',
    name: 'Easing Functions',
    description:
      'Visualize and compare CSS timing functions with live animation previews. See how ease, linear, cubic-bezier, and steps affect motion to create smooth, professional animations.',
    category: 'code',
    type: 'internal',
    path: '/tool/easing-functions',
    icon: 'TrendingUp',
  },
  {
    id: 'mock-api-generator',
    name: 'Mock API Generator',
    description:
      'Create mock API endpoints with custom JSON responses, configurable delays, and HTTP status codes. Develop frontend features without a backend by simulating realistic API behavior locally.',
    category: 'code',
    type: 'internal',
    path: '/tool/mock-api-generator',
    icon: 'Server',
  },
  {
    id: 'websocket-tester',
    name: 'WebSocket Tester',
    description:
      'Test WebSocket connections in real-time with message history, auto-reconnect, and bidirectional communication. Debug WebSocket implementations, chat apps, and live data streams with full connection control.',
    category: 'code',
    type: 'internal',
    path: '/tool/websocket-tester',
    icon: 'Wifi',
  },
  {
    id: 'api-request-builder',
    name: 'API Request Builder',
    description:
      'Build and test HTTP/REST API requests with authentication (Bearer, Basic), custom headers, and request bodies. Save frequently-used requests to collections and track history like a browser-based Postman alternative.',
    category: 'code',
    type: 'internal',
    path: '/tool/api-request-builder',
    icon: 'Send',
  },
  {
    id: 'gitignore-generator',
    name: '.gitignore Generator',
    description:
      'Generate .gitignore files from language and framework templates (Node, Python, Java, etc.). Quickly exclude build artifacts, dependencies, IDE files, and sensitive data with community-maintained patterns.',
    category: 'code',
    type: 'external',
    url: 'https://www.toptal.com/developers/gitignore',
    icon: 'FileX',
  },
  {
    id: 'regex-tester',
    name: 'Regex Tester',
    description:
      'Test regular expressions with live match highlighting and instant feedback. Validate regex patterns against sample text to debug capture groups, flags, and match behavior before using in code.',
    category: 'code',
    type: 'external',
    url: 'https://regex101.com/',
    icon: 'Regex',
  },
  {
    id: 'js-minifier',
    name: 'JS Minifier',
    description:
      'Minify JavaScript to reduce bundle size for production or beautify minified code for debugging. Toggle between compressed and readable formats to optimize performance or understand third-party libraries.',
    category: 'code',
    type: 'external',
    url: 'https://javascript-minifier.com/',
    icon: 'FileCode2',
  },
  {
    id: 'caniuse',
    name: 'Can I Use',
    description:
      'Check browser support and compatibility data for HTML, CSS, JavaScript, and web platform features. Prevent compatibility issues by verifying feature support across browsers and versions before implementation.',
    category: 'code',
    type: 'external',
    url: 'https://caniuse.com/',
    icon: 'Globe',
  },
  {
    id: 'bundlephobia',
    name: 'Bundlephobia',
    description:
      'Analyze npm package size, dependencies, and bundle impact before installing. Optimize bundle size and performance by comparing alternatives and understanding the cost of each dependency.',
    category: 'code',
    type: 'external',
    url: 'https://bundlephobia.com/',
    icon: 'Package',
  },
  {
    id: 'graphql-query-builder',
    name: 'GraphQL Query Builder',
    description:
      'Visual GraphQL query and mutation builder with schema input, variables editor, fragments support, and introspection query helper. Build complex queries without memorizing syntax.',
    category: 'code',
    type: 'internal',
    path: '/tool/graphql-query-builder',
    icon: 'Network',
  },

  // Crypto Tools
  {
    id: 'hash-generator',
    name: 'Hash Generator',
    description:
      'Generate cryptographic hashes (MD5, SHA-1, SHA-256, SHA-384, SHA-512) from text or files with optional HMAC support and multiple output formats',
    category: 'crypto',
    type: 'internal',
    path: '/tool/hash-generator',
    icon: 'Hash',
  },
  {
    id: 'uuid-generator',
    name: 'UUID Generator',
    description:
      'Generate RFC 4122 compliant v4 UUIDs with bulk generation (up to 1,000), multiple export formats (TXT, JSON, CSV), and one-click copy functionality',
    category: 'crypto',
    type: 'internal',
    path: '/tool/uuid-generator',
    icon: 'Key',
  },
  {
    id: 'password-generator',
    name: 'Password Generator',
    description:
      'Generate cryptographically secure passwords using Web Crypto API with customizable length, character types, entropy display, bulk generation, and multiple download formats',
    category: 'crypto',
    type: 'internal',
    path: '/tool/password-generator',
    icon: 'KeyRound',
  },
  {
    id: 'password-strength-checker',
    name: 'Password Strength Checker',
    description:
      'Analyze password security with entropy calculation, crack time estimation, pattern detection (keyboard patterns, common passwords, sequences), and actionable improvement suggestions',
    category: 'crypto',
    type: 'internal',
    path: '/tool/password-strength-checker',
    icon: 'ShieldCheck',
  },
  {
    id: 'random-number-generator',
    name: 'Random Number Generator',
    description:
      'Generate random numbers with min/max range, multiple values, unique numbers option, cryptographically secure mode using Web Crypto API, distribution preview with statistics, and dice roller mode with d6/d20/d100 presets',
    category: 'crypto',
    type: 'internal',
    path: '/tool/random-number-generator',
    icon: 'Dices',
  },
  {
    id: 'checksum-calculator',
    name: 'Checksum Calculator',
    description:
      'Calculate and verify file integrity checksums (CRC32, CRC16, CRC8, CRC-CCITT, Adler-32, Fletcher-16) with drag-and-drop file support, verification mode, and side-by-side file comparison',
    category: 'crypto',
    type: 'internal',
    path: '/tool/checksum-calculator',
    icon: 'Calculator',
  },
  {
    id: 'rsa-key-generator',
    name: 'RSA Key Generator',
    description:
      'Generate production-ready RSA key pairs (1024-4096 bit) with multiple export formats (PKCS#8, PKCS#1, OpenSSH, JWK, DER), passphrase protection, fingerprints, and downloadable ZIP bundles',
    category: 'crypto',
    type: 'internal',
    path: '/tool/rsa-key-generator',
    icon: 'KeySquare',
  },
  {
    id: 'totp-generator',
    name: 'TOTP Generator',
    description:
      'Generate Time-based One-Time Passwords (TOTP) for two-factor authentication with QR code display, multiple accounts management, backup codes generator, and configurable algorithms (SHA-1/256/512)',
    category: 'crypto',
    type: 'internal',
    path: '/tool/totp-generator',
    icon: 'Shield',
  },
  {
    id: 'bcrypt-generator',
    name: 'Bcrypt Generator',
    description:
      'Generate and verify bcrypt password hashes with adjustable cost factor (4-12 rounds), timing analysis, hash structure visualization, and secure password verification',
    category: 'crypto',
    type: 'internal',
    path: '/tool/bcrypt-generator',
    icon: 'ShieldCheck',
  },
  {
    id: 'caesar-cipher',
    name: 'ROT13 / Caesar Cipher',
    description:
      'Encode and decode text using ROT13 or Caesar cipher (offset 1-25) with brute force attack simulation, multilingual frequency analysis (English, Polish, German, Spanish, French), and auto-detection',
    category: 'crypto',
    type: 'internal',
    path: '/tool/caesar-cipher',
    icon: 'KeyRound',
  },
  {
    id: 'aes-encryption',
    name: 'AES Encryption/Decryption',
    description:
      'Encrypt and decrypt text or files using AES-256-GCM with password-based key derivation (PBKDF2), IV display, password strength indicator, and file export/import support',
    category: 'crypto',
    type: 'internal',
    path: '/tool/aes-encryption',
    icon: 'Lock',
  },
  {
    id: 'hmac-generator',
    name: 'HMAC Generator',
    description:
      'Generate and verify Hash-based Message Authentication Codes with SHA-1/SHA-256/SHA-512 algorithms. Supports batch generation, HMAC verification, hex/base64 output formats, and visual comparison of HMACs',
    category: 'crypto',
    type: 'internal',
    path: '/tool/hmac-generator',
    icon: 'Shield',
  },
  {
    id: 'jwt-decoder',
    name: 'JWT Decoder',
    description:
      'Decode and inspect JSON Web Tokens with header/payload parsing, signature verification, claims validation, and expiration checking',
    category: 'crypto',
    type: 'external',
    url: 'https://jwt.io/',
    icon: 'Shield',
  },

  // Misc Tools
  {
    id: 'meta-tags-generator',
    name: 'Meta Tags Generator',
    description:
      'Generate and preview SEO meta tags, Open Graph, and Twitter Card tags for optimal social media sharing and search engine visibility',
    category: 'misc',
    type: 'internal',
    path: '/tool/meta-tags-generator',
    icon: 'Tags',
  },
  {
    id: 'emoji-picker',
    name: 'Emoji Picker',
    description:
      'Browse and copy emojis by category with instant search, perfect for documentation, commit messages, and UI content',
    category: 'misc',
    type: 'internal',
    path: '/tool/emoji-picker',
    icon: 'Smile',
  },
  {
    id: 'ascii-table',
    name: 'ASCII Table',
    description:
      'Complete ASCII reference table with decimal, hexadecimal, and character representations for debugging character encoding issues',
    category: 'misc',
    type: 'internal',
    path: '/tool/ascii-table',
    icon: 'Table2',
  },
  {
    id: 'html-entity-list',
    name: 'HTML Entity List',
    description:
      'Searchable reference of HTML entities and special characters with one-click copy for safely rendering symbols in web pages',
    category: 'misc',
    type: 'internal',
    path: '/tool/html-entity-list',
    icon: 'Code',
  },
  {
    id: 'device-screen-sizes',
    name: 'Device Screen Sizes',
    description:
      'Quick reference guide for device viewports, screen resolutions, and pixel densities to inform responsive design breakpoints',
    category: 'misc',
    type: 'internal',
    path: '/tool/device-screen-sizes',
    icon: 'Monitor',
  },
  {
    id: 'barcode-generator',
    name: 'Barcode Generator',
    description:
      'Create production-ready barcodes in EAN-13, UPC-A, and Code128 formats with bulk generation and downloadable images for e-commerce and inventory systems',
    category: 'misc',
    type: 'internal',
    path: '/tool/barcode-generator',
    icon: 'Barcode',
  },
  {
    id: 'open-graph-preview',
    name: 'Open Graph Preview',
    description:
      'Preview how your content appears on Facebook, Twitter, and LinkedIn with live Open Graph tag generation and validation',
    category: 'misc',
    type: 'internal',
    path: '/tool/open-graph-preview',
    icon: 'Share2',
  },
  {
    id: 'qr-code-reader',
    name: 'QR Code Reader',
    description:
      'Decode QR codes from images or webcam with vCard parsing and batch processing support for testing QR implementations',
    category: 'misc',
    type: 'internal',
    path: '/tool/qr-code-reader',
    icon: 'ScanLine',
  },
  {
    id: 'google-fonts-browser',
    name: 'Google Fonts Browser',
    description:
      'Browse and preview 1500+ Google Fonts with real-time search, category filtering (Serif, Sans Serif, Display, Handwriting, Monospace), font pairing suggestions, live preview with custom text, and export as HTML link tags, CSS @import, or font-family declarations.',
    category: 'misc',
    type: 'internal',
    path: '/tool/google-fonts-browser',
    icon: 'Type',
  },
  {
    id: 'qr-generator',
    name: 'QR Code Generator',
    description: 'Generate QR codes for URLs and text',
    category: 'misc',
    type: 'external',
    url: 'https://www.qr-code-generator.com/',
    icon: 'QrCode',
  },
  {
    id: 'markdown-preview',
    name: 'Markdown Preview',
    description: 'Preview markdown in real-time',
    category: 'misc',
    type: 'external',
    url: 'https://markdownlivepreview.com/',
    icon: 'FileText',
  },

  // Network Tools
  {
    id: 'url-parser',
    name: 'URL Parser',
    description:
      'Parse URLs into components (protocol, hostname, port, path, query, hash) with query parameter extraction. Toggle between encoded/decoded views, build URLs from components, validate URLs, and detect schemes (http, https, ftp, mailto, tel, ws, ssh, file, data). Two-tab interface with example URLs for quick testing.',
    category: 'network',
    type: 'internal',
    path: '/tool/url-parser',
    icon: 'Link',
  },
  {
    id: 'http-headers-viewer',
    name: 'HTTP Headers Viewer',
    description:
      'Fetch and analyze HTTP headers from any URL. View request/response headers, response metadata (status, timing, protocol), security header analysis with scoring (HSTS, CSP, X-Frame-Options, etc.), custom request headers, CORS detection, redirect chain tracking, and recommendations for missing security headers.',
    category: 'network',
    type: 'internal',
    path: '/tool/http-headers-viewer',
    icon: 'Globe',
  },

  // SEO Tools
  {
    id: 'utm-builder',
    name: 'UTM Builder',
    description:
      'Build URLs with UTM tracking parameters for campaign analytics. Generate single or bulk URLs with source, medium, campaign, term, and content parameters. Includes 8 saved templates (email, Facebook, Twitter, LinkedIn, Instagram, Google Ads), QR code generator, bulk CSV import, and Google Analytics preview.',
    category: 'seo',
    type: 'internal',
    path: '/tool/utm-builder',
    icon: 'BarChart3',
  },
  {
    id: 'social-media-sizes',
    name: 'Social Media Image Sizes',
    description:
      'Complete reference guide for social media image dimensions across 8 major platforms (Facebook, Instagram, Twitter, LinkedIn, YouTube, Pinterest, TikTok, Snapchat). Includes profile pictures, cover photos, posts, stories, and ads with exact dimensions, aspect ratios, recommended formats, and file size limits for optimal display.',
    category: 'seo',
    type: 'internal',
    path: '/tool/social-media-sizes',
    icon: 'Image',
  },
];

export function getToolsByCategory(category: Category, favoriteIds?: string[]): Tool[] {
  if (category === 'all') return tools;
  if (category === 'favorites') {
    return favoriteIds ? tools.filter((tool) => favoriteIds.includes(tool.id)) : [];
  }

  return tools.filter((tool) => tool.category === category);
}

export function getToolById(id: string): Tool | undefined {
  return tools.find((tool) => tool.id === id);
}

export function getCategoryById(id: Category) {
  return categories.find((cat) => cat.id === id);
}
